class HeaderManager {
    constructor() {
        this.resolutionThreshold = 1366;
        this.desktopHeaderClass = "voyager-header--desktop";
        this.mobileHeaderClass = "voyager-header--mobile";
    }

    // Check if the browser is Safari
    isSafariBrowser() {
        const userAgent = navigator.userAgent;
        return /^((?!chrome|android).)*safari/i.test(userAgent);
    }

    // Check if the device is touch-enabled
    isTouchEnabledDevice() {
        return "ontouchstart" in window || navigator.maxTouchPoints > 0;
    }

    // Check if the resolution is within the threshold
    isResolutionWithinThreshold() {
        return window.innerWidth <= this.resolutionThreshold;
    }

    // Determine and remove the appropriate header
    manageHeaders() {
        const desktopHeader = document.querySelector(
            `.${this.desktopHeaderClass}`
        );
        const mobileHeader = document.querySelector(
            `.${this.mobileHeaderClass}`
        );

        // Ensure both headers exist before proceeding
        if (!desktopHeader || !mobileHeader) return false;

        const isSafari = this.isSafariBrowser();
        const isTouchEnabled = this.isTouchEnabledDevice();
        const isResolutionValid = this.isResolutionWithinThreshold();

        // Check all conditions
        if (isSafari && isTouchEnabled && isResolutionValid) {
            // Remove the desktop header
            desktopHeader.remove();
            mobileHeader.style.visibility = "visible";
            console.log("Desktop header removed.");
        } else {
            // Remove the mobile header
            mobileHeader.remove();
            desktopHeader.style.visibility = "visible";
            console.log("Mobile header removed.");
        }
    }
}

const headerManager = new HeaderManager();
headerManager.manageHeaders();

class CreateFirstLevelNavigation {
    constructor(options) {
        this.defaultOptions = {
            menuName: "voyager-header",
            accordionName: "js-voyager-accordion",
            firstLevelListElementActiveClass: "active-menu-ancestor",
            get firstLevelMenuName() {
                return `${this.menuName}__horizontal-menu`;
            },
            get menuSelector() {
                return `.${this.menuName}`;
            },
            get menuWrapperSelector() {
                return `.${this.menuName}__menu-wrapper`;
            },
            get firstLevelMenuItemSelector() {
                return `.${this.menuName}__menu-item-level-0`;
            },
            get firstLevelMenuItemAnchorContainerSelector() {
                return `.${this.menuName}__menu-item-container`;
            },
        };

        this.options = Object.assign(this.defaultOptions, options);
    }

    validate(menu) {
        const { menuName, menuWrapperSelector, firstLevelMenuItemSelector } =
            this.options;
        const menuWrapper = menu.querySelector(menuWrapperSelector);
        const firstLevelMenuItems = menu.querySelectorAll(
            firstLevelMenuItemSelector
        );
        if (
            !menu.classList.contains(`${menuName}--mobile`) ||
            !menuWrapper ||
            !firstLevelMenuItems.length
        )
            return false;

        const validatedSelector = {
            menuWrapper,
            firstLevelMenuItems,
        };
        return validatedSelector;
    }

    addWrapperElement(validatedSelector) {
        const { menuWrapper } = validatedSelector;
        const { menuName } = this.options;

        // Select the two menus
        const headerMenu = menuWrapper.querySelector(`.${menuName}__menu`);
        const footerMenu = menuWrapper.querySelector(`.voyager-footer__menu`);

        // Ensure element is exist
        if (!headerMenu) return false;

        // Create the wrapper div and set its class
        const wrapper = document.createElement("div");
        wrapper.className = `${menuName}__menu-scrollable-wrapper`;

        // Insert the wrapper into the DOM in place of the header menu
        const parent = headerMenu.parentElement;
        parent.insertBefore(wrapper, headerMenu);

        // Move the header menu and footer menu into the wrapper
        wrapper.appendChild(headerMenu);
        footerMenu && wrapper.appendChild(footerMenu);
    }

    initInstance(menu) {
        const validatedSelector = this.validate(menu);
        if (!validatedSelector) return false;

        const { menuWrapper, firstLevelMenuItems } = validatedSelector;
        const { menuName, firstLevelMenuName, accordionName } = this.options;

        const newMenuElement = document.createElement("ul");
        newMenuElement.className = `${firstLevelMenuName} js-voyager-accordion`;

        const {
            firstLevelMenuItemAnchorContainerSelector,
            firstLevelListElementActiveClass,
        } = this.options;
        firstLevelMenuItems.forEach((menuItem, index) => {
            const anchorContainer = menuItem.querySelector(
                firstLevelMenuItemAnchorContainerSelector
            );
            if (!anchorContainer) return;

            const newMenuItemElement = document.createElement("li");
            newMenuItemElement.className = `${firstLevelMenuName}-item`;

            const anchor = anchorContainer.querySelector(
                `.${menuName}__menu-anchor`
            );
            const triggerButton = anchorContainer.querySelector(
                `.${accordionName}__trigger`
            );
            const anchorClasses = triggerButton
                ? `${firstLevelMenuName}-anchor ${accordionName}__trigger`
                : `${firstLevelMenuName}-anchor`;
            const anchorLink = triggerButton
                ? "javascript:void(0)"
                : anchor.href;

            // Remove trigger button from the first level menu
            triggerButton?.remove();

            const anchorTemplateString = `<a class="${anchorClasses}" href="${anchorLink}" ${
                triggerButton
                    ? `id="${
                          triggerButton.id
                      }" aria-controls="${triggerButton.getAttribute(
                          "aria-controls"
                      )}"`
                    : ""
            }>${anchor.textContent}</a>`;

            newMenuItemElement.innerHTML = anchorTemplateString;
            newMenuElement.appendChild(newMenuItemElement);
        });

        menuWrapper.insertAdjacentElement("afterbegin", newMenuElement);

        // Activate first level menu item
        const firstLevelMenuTriggers = newMenuElement.querySelectorAll(
            `.${accordionName}__trigger`
        );
        firstLevelMenuTriggers?.forEach((trigger) => {
            trigger.addEventListener("click", (e) => {
                const targetPanelId = e.target.getAttribute("aria-controls");
                if (!targetPanelId) return;

                const targetPanel = document.querySelector(`#${targetPanelId}`);
                if (!targetPanel) return;

                const targetPanelAncestor = targetPanel.closest(
                    `.${menuName}__menu-item-level-0`
                );
                if (!targetPanelAncestor) return;

                // Remove active class from all triggers
                firstLevelMenuTriggers?.forEach((triggerButton) => {
                    triggerButton.classList.remove("is-active");
                });

                // Get target panel siblings and remove active class
                const siblings = targetPanelAncestor.parentNode.children;

                [...siblings]?.forEach((sibling) => {
                    sibling.classList?.remove(firstLevelListElementActiveClass);
                });

                targetPanelAncestor.classList.add(
                    firstLevelListElementActiveClass
                );
            });
        });
        // Activate first level submenu item on clicking on Hamburger
        document
            .querySelectorAll(`.${menuName}__hamburger-button`)
            ?.forEach((hamburgerButton) => {
                hamburgerButton.addEventListener("click", () => {
                    firstLevelMenuTriggers[0].classList.contains(
                        `${accordionName}__trigger`
                    ) && firstLevelMenuTriggers[0].click();
                });
            });

        // Add wrapper element for scrolling
        this.addWrapperElement(validatedSelector);
    }

    init() {
        const { menuSelector } = this.options;
        document.querySelectorAll(menuSelector)?.forEach((menu) => {
            this.initInstance(menu);
        });
    }
}

const createFirstLevelNavigation = new CreateFirstLevelNavigation();
createFirstLevelNavigation.init();

function detectPageAndAddHeaderHelperClass() {
    const isLandingPage = window.location.pathname === "/";

    const headers = document.querySelectorAll(".voyager-header");

    headers?.forEach((header) => {
        !header.querySelector(".voyager-header__menu-anchor--is-active") &&
            header.classList.add("voyager-header--unknown-page");

        if (isLandingPage) header.classList.add("voyager-header--landing-page");

        header.classList.add("voyager-header--dom-content-loaded");
    });
}

document.addEventListener(
    "DOMContentLoaded",
    detectPageAndAddHeaderHelperClass
);

class HeaderMenu {
    constructor() {
        this.menu = document.querySelector(
            ".voyager-header--desktop .voyager-header__menu "
        );
        if (!this.menu) return;
        this.submenu_2 = this.menu.querySelectorAll(
            ".voyager-header__submenu-level-2"
        );
        if (this.submenu_2) {
            this.submenu_2.forEach((item) => {
                this.output_html = "";
                this.submenuLevel2(item);
                var new_html = `<div class="voyager-header__submenu-card">
                                    ${
                                        item.children.length !== 0
                                            ? item.outerHTML
                                            : ""
                                    }
                                    <div class="voyager-header__menu-item-cards">
                                        ${this.output_html}
                                    </div>
                                </div>`;
                item.insertAdjacentHTML("afterend", new_html);
                item.remove();

                // var new_html = '<li class="voyager-header__menu-item voyager-header__menu-item-level-2 voyager-header__submenu-item voyager-header__menu-item-last">'+ this.output_html +'</li>';
                // item.insertAdjacentHTML('beforeend', new_html)
            });
        }
    }

    submenuLevel2(item) {
        var items = item.children;

        this.lastItem(items[items.length - 1]);
        this.lastItem(items[items.length - 1]);
    }

    lastItem(item) {
        if (!item) return;
        var feature = item.dataset.featureImage,
            anchor = item.querySelector(".voyager-header__menu-anchor"),
            url = anchor.href,
            text = anchor.innerHTML;
        this.output_html += `<div class="voyager-header__menu-item-card">
                                <a href="${url}" class="voyager-header__menu-item-card-link voyager-header__menu-item-card-link-level-2">
                                    <span class="voyager-header__menu-item-card-text">${text}<i class="voyager-header__menu-item-card-text-icon voyager-icon-arrow-pointing-right-small voyager-social-links__icon"></i></span>
                                    <div class="voyager-header__menu-item-card-image-wrapper">
                                        <img src="${feature}" alt="" class="voyager-header__menu-item-card-image">
                                    </div>
                                </a>
                            </div>`;
        item.remove();
    }
}

new HeaderMenu();
